import { css } from '@linaria/core'
import { styled } from '@linaria/react'
import {
  BaseCardSC,
  CardChildContentSC,
  TopCardBaseSC,
} from 'blocks/Cards/base-card/BaseCard.styled'
import {
  cardHoverFavoriteButtonStyles,
  cardHoverFavoriteSlotStyles,
} from 'blocks/Cards/cardHoverFavorite.styled'
import {
  COLOR,
  CORNERS,
  FIXED_INDENTS,
  ICONS_SIZES,
  MEDIA,
  TRANSITIONS,
} from 'vars/index'
import { Card } from '../Card'
import { CardPosterSize } from '../Card.h'

/**
 * Design review on FP-4057 asks for two things at once: the New badge paints on
 * top of the catch-up copy, and every label stays under the hover state layer.
 * That fixes the order to copy < corner labels < hover layer < hover controls,
 * which is also how Figma models the card (node 18488-231275 is a `body` frame
 * plus a `state-layer` frame holding the hover controls only).
 *
 * `CardChildContentSC` defaults to `z-index: 100`, whose stacking context would
 * put the programme copy above the labels and above the hover layer, and would
 * clamp the my-list slot below both. Dropping it to `auto` restores BaseCard's
 * order inside `BaseCardSC`: unpositioned copy first, then the labels
 * (`z-index: 1`), the hover layer (`PosterContainerSC::after`, `z-index: 2`) and
 * `ProgrammeCardFavoriteSC` (`z-index: 102`), which is positioned and so joins
 * that same context. The copy dimming with the card on hover follows from the
 * order — it is not separately configurable.
 */
export const ProgrammeCardRootSC = styled(Card)`
  && ${CardChildContentSC} {
    z-index: auto;
  }
`

/**
 * Card body: a centred description and, below it, the progress indicator. The
 * padding lives on those two blocks instead of here, so the Watch Now progress
 * track can span the whole card width (UI-5.6).
 */
export const ProgrammeCardSC = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`

export const ProgrammeCardDescriptionSC = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(${FIXED_INDENTS.INDENT_20});
  padding: 0 var(${FIXED_INDENTS.INDENT_40});
  overflow: hidden;
`

export const ProgrammeCardChannelSC = styled.div`
  display: flex;
  align-items: center;
  gap: var(${FIXED_INDENTS.INDENT_40});
  min-width: 0;
  /* logo-less row has no sized image to pin height; do not shrink under the title */
  flex-shrink: 0;

  & > *:last-child {
    min-width: 0;
  }
`

/**
 * Figma reserves two title lines (h-48 / 2×lh-sm) and does not shrink them.
 * `maxLines` sets overflow:hidden on TextBox, which zeroes the flex min-size
 * and lets the second line clip mid-glyph. `2lh` tracks FontSmBold (constant 24px).
 */
export const programmeCardTitleStyles = css`
  flex-shrink: 0;
  min-height: 2lh;
`

/**
 * Figma `container-logo`: 48px circle by default (below 1240 and 1640+), 40px
 * at 1240–1639 (FP-4306). Dark-25 fill, 0.2px light-15 border. Inner logo
 * uses the same 70.7% ratio as the TV channel circular poster.
 */
export const ProgrammeCardLogoContainerSC = styled.div`
  width: var(${ICONS_SIZES.SIZE_40});
  height: var(${ICONS_SIZES.SIZE_40});
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: var(${CORNERS.CORNERS_ROUND});
  background-color: var(${COLOR.SURFACE_OPACITY_DARK_25});
  border: 0.2px solid var(${COLOR.SURFACE_OPACITY_LIGHT_15});

  ${MEDIA.MIN_1240} {
    width: var(${FIXED_INDENTS.INDENT_100});
    height: var(${FIXED_INDENTS.INDENT_100});
  }

  ${MEDIA.MIN_1640} {
    width: var(${ICONS_SIZES.SIZE_40});
    height: var(${ICONS_SIZES.SIZE_40});
  }
`

export const ProgrammeCardLogoSC = styled.img`
  width: ${() => `${CardPosterSize.TvSimplePosterWidth}%`};
  height: ${() => `${CardPosterSize.TvSimplePosterWidth}%`};
  object-fit: contain;
`

/**
 * Watch Now keeps the progress track flush with the card edge, so only the
 * catch-up variant — which has a date-time label and no track — needs bottom
 * padding (UI-5.7, UI-5.8).
 */
export const ProgrammeCardFooterSC = styled.div<{ withProgress: boolean }>`
  display: flex;
  flex-direction: column;
  gap: var(${FIXED_INDENTS.INDENT_20});
  width: 100%;
  flex-shrink: 0;
  margin-top: auto;
  padding-bottom: ${({ withProgress }) =>
    withProgress ? '0' : `var(${FIXED_INDENTS.INDENT_30})`};
`

export const ProgrammeCardTimeSC = styled.div<{ alignEnd: boolean }>`
  display: flex;
  justify-content: ${({ alignEnd }) => (alignEnd ? 'flex-end' : 'flex-start')};
  min-width: 0;
  min-height: 1lh;
  width: 100%;
  padding: 0 var(${FIXED_INDENTS.INDENT_40});
`

/** My-list button slot for the channel-row / Watch Now hover layout (UI-5.1/5.2). */
export const ProgrammeCardFavoriteSC = styled.div`
  position: absolute;
  top: var(${FIXED_INDENTS.INDENT_20});
  right: var(${FIXED_INDENTS.INDENT_20});
  z-index: 102;
  opacity: 0;
  pointer-events: none;
  transition: opacity var(${TRANSITIONS.LINEAR_400});
  ${cardHoverFavoriteSlotStyles}
  ${cardHoverFavoriteButtonStyles}

  ${MEDIA.NO_TOUCH} {
    ${BaseCardSC}:hover &,
    ${TopCardBaseSC}:hover & {
      opacity: 1;
      pointer-events: auto;
    }
  }

  ${MEDIA.TOUCH} {
    display: none;
  }
`
